import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { LiaSpinnerSolid } from "react-icons/lia";
import { FaRegFolder } from "react-icons/fa6";
import { BsFileEarmarkPdf } from "react-icons/bs";
import { IoIosCloudDone } from 'react-icons/io';
import { MdCancel } from 'react-icons/md';
import { TiTick } from 'react-icons/ti';
import { RxCrossCircled } from 'react-icons/rx';
import { AiOutlineFileDone } from 'react-icons/ai';

import ApproveQAToasterComponent from '../consultanttempatefillIncomponents/QAToasters/ApproveQAToaster';
import RejectQAToasterComponent from '../consultanttempatefillIncomponents/QAToasters/RejectQAToaster';

const TemplateB = ({
  manualTemplates,
  selectedSubmissionId,
  selectedCompany,
  selectedCompanyName,
  selectedDepartment,
  selectedDepartmentName,
  onRefreshTemplates,
  showToaster,
  setShowToaster,
  setErrorMessage
}) => {

  // ----------------- State Management -----------------
  
  // Employee data state
  const [employees, setEmployees] = useState({});
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Employee document state
  const [viewingEmployeeDocument, setViewingEmployeeDocument] = useState(null);
  const [loadingEmployeeDocument, setLoadingEmployeeDocument] = useState(false);
  const [employeeDocuments, setEmployeeDocuments] = useState({});

  // State Management Employee - Toasters
  const [selectedEmployeeForQA, setSelectedEmployeeForQA] = useState(null);
  const [showEmployeeApproveToaster, setShowEmployeeApproveToaster] = useState(false);
  const [showEmployeeRejectToaster, setShowEmployeeRejectToaster] = useState(false);

  const [fullScreenImage, setFullScreenImage] = useState(null);

  /*-----------------------------------------------------------------
                            Use Effects 
  ---------------------------------------------------------------*/

  const fetchEmployeesForTemplate = async (template) => {
  try {
    setLoadingEmployees(true);
    
    const response = await axios.get(
      `/api/compliance-tracker/quality-assurance/fetch-employees/${selectedCompany}/${selectedDepartment}/${template.template_reference}`
    );
    
    const employeesData = response.data || [];
    
    // Store employees data with template ID as key
    setEmployees(prev => ({
      ...prev,
      [template.template_id]: employeesData
    }));
    
    setLoadingEmployees(false);
    
  } catch (error) {
    console.error('Error fetching employees:', error);
    setErrorMessage('Failed to load employees. Please try again.');
    setLoadingEmployees(false);
    
    // Set empty array for this template on error
    setEmployees(prev => ({
      ...prev,
      [template.template_id]: []
    }));
  }
};

// Separate function to fetch employee document responses
const fetchEmployeeDocumentResponses = async (templateId) => {
  try {
    const response = await axios.get(
      `/api/compliance-tracker/quality-assurance/fetch-employee-document-responses/${selectedCompany}/${selectedDepartment}/${templateId}`
    );
    
    const documentResponses = response.data || [];
    
    // Update employees with their document responses
    setEmployees(prev => {
      const updatedEmployees = { ...prev };
      if (updatedEmployees[templateId]) {
        // Merge document responses with existing employee data
        updatedEmployees[templateId] = updatedEmployees[templateId].map(employee => {
          const documentResponse = documentResponses.find(doc => doc.employee_id === employee.employee_id);
          return documentResponse ? { ...employee, ...documentResponse } : employee;
        });
      }
      return updatedEmployees;
    });
    
  } catch (error) {
    console.error('Error fetching employee document responses:', error);
    setErrorMessage('Failed to load employee document responses. Please try again.');
  }
};

// Updated useEffect to call both functions
useEffect(() => {
  if (manualTemplates.length > 0) {
    manualTemplates.forEach(async template => {
      // First fetch employees
      await fetchEmployeesForTemplate(template);
      // Then fetch their document responses
      await fetchEmployeeDocumentResponses(template.template_id);
    });
  }
}, [manualTemplates, selectedCompany, selectedDepartment]);

  /*----------------------------------------------------
                      Functions
  ------------------------------------------------------*/

  // Employee Approve and Reject Click Handlers
  const handleEmployeeApproveClick = (employee, templateId) => {
    setSelectedEmployeeForQA({ ...employee, templateId });
    setShowEmployeeApproveToaster(true);
  };

  const handleEmployeeRejectClick = (employee, templateId) => {
    setSelectedEmployeeForQA({ ...employee, templateId });
    setShowEmployeeRejectToaster(true);
  };

  const handleEmployeeApproveClose = () => {
    setShowEmployeeApproveToaster(false);
    setSelectedEmployeeForQA(null);
  };

  const handleEmployeeRejectClose = () => {
    setShowEmployeeRejectToaster(false);
    setSelectedEmployeeForQA(null);
  };

  // Function Employee Reject - Reject the consultant template submissions. 
  const handleEmployeeRejectSubmit = async (reason, documentId) => {

    try {
      const response = await axios.post(`/api/compliance-tracker/quality-asssurance/review-consultant-submissions/employee/reject/${selectedSubmissionId}`, {
        employeeId: documentId,
        empDocumentId: selectedEmployeeForQA?.employee_document_id,
        empQaComment: reason
      });

      if (response.data.success) {
        console.log('Employee document rejected successfully');
        
        // Show success message
        setShowToaster(prev => ({ ...prev, employeeRejected: true }));

        // Close the reject toaster
        setTimeout(() => handleEmployeeRejectClose(), 3000);

        onRefreshTemplates();

        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error rejecting employee document:', error);
      setErrorMessage('Failed to reject employee document. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
      return Promise.reject(error);
    }
  };

  // Function Employee Approve - Approve the consultant template submissions. 
  const handleEmployeeApproveSubmit = async (documentId) => {
    try {
      const response = await axios.post('/api/compliance-tracker/quality-asssurance/review-consultant-submissions/employee/approve', {
        employeeId: documentId,
        empDocumentId: selectedEmployeeForQA?.employee_document_id,
      
      });

      if (response.data.success) {
        console.log('Employee document approved successfully');
        
        // Show success message
        setShowToaster(prev => ({ ...prev, employeeApproved: true }));

        // Close the approve toaster
        setTimeout(() => handleEmployeeApproveClose(), 3000);

        onRefreshTemplates();

        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error approving employee document:', error);
      setErrorMessage('Failed to approve employee document. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
      return Promise.reject(error);
    }
  };

  
  // ----------------- Employee Document Functions ----------------
  const fetchEmployeeDocument = async (employeeId, templateId) => {
    try {
      setLoadingEmployeeDocument(true);
      const compositeKey = `${templateId}-${employeeId}`;
      
      // Check if we already have the document cached
      if (employeeDocuments[compositeKey]) {
        setViewingEmployeeDocument(employeeDocuments[compositeKey]);
        setLoadingEmployeeDocument(false);
        return;
      }
      
      // Fetch the document from API
      const response = await axios.get(
        `/api/compliance-tracker/quality-assurance/file-and-template-viewer/view-template/employee-documents/${employeeId}/${templateId}`
      );
      
      if (response.data && response.data.file_response) {
        setEmployeeDocuments(prev => ({
          ...prev,
          [compositeKey]: response.data
        }));
        setViewingEmployeeDocument(response.data);
      } else {
        setErrorMessage("No document found for this employee.");
        setTimeout(() => setErrorMessage(''), 3000);
      }
      setLoadingEmployeeDocument(false);
    } catch (error) {
      console.error('Error fetching employee document:', error);
      setErrorMessage('Failed to load employee document. Please try again.');
      setLoadingEmployeeDocument(false);
    }
  };

  const handleViewEmployeeDocument = (employee, templateId) => {
    setFullScreenImage(null);
    fetchEmployeeDocument(employee.employee_id, templateId);
  };
  
  const closeEmployeeDocumentViewer = () => {
    setViewingEmployeeDocument(null);
    setFullScreenImage(null);
  };

  // Image preview handling
  const openFullScreenImage = (imageUrl) => {
    setFullScreenImage(imageUrl);
  };

  const closeFullScreenImage = () => {
    setFullScreenImage(null);
  };

  /*----------------------------------------------------------
                        Document Rendering 
  ------------------------------------------------------------*/
  const bufferToBase64 = (buffer) => {
    if (buffer && buffer.type === 'Buffer' && Array.isArray(buffer.data)) {
      const binaryString = buffer.data.map(byte => String.fromCharCode(byte)).join('');
      return btoa(binaryString);
    }
    return null;
  };

  const renderEmployeeDocument = (document) => {
    // Check if document exists and has file_response
    if (!document || !document.file_response) {
      return <p>No file available</p>;
    }

    // Check if file_name exists for PDF detection
    if (!document.file_name) {
      return <p>File name not available</p>;
    }

    // Check if the file is a PDF
    const isPdf = document.file_name.toLowerCase().endsWith('.pdf');
    
    if (isPdf) {
      const base64String = bufferToBase64(document.file_response);
      if (!base64String) {
        return <p>Invalid PDF data</p>;
      }

      const base64ToBinary = atob(base64String);
          
      return (
        <div className="pdf-container">
          <BsFileEarmarkPdf 
            className='view-checklist-view-image-icon'
            onClick={() => {
              // Create a new window
              const pdfWindow = window.open('', '_blank');
                          
              // Write the PDF content directly to the new window
              pdfWindow.document.write(
                `<html>
                  <head>
                    <title>${document.file_name}</title>
                  </head>
                  <body style="margin: 0;">
                    <iframe
                       src="data:application/pdf;base64,${base64ToBinary}"
                       width="100%"
                       height="100%"
                       style="border: none;"
                    ></iframe>
                  </body>
                </html>`
              );
              pdfWindow.document.close();
            }}
            style={{ cursor: 'pointer' }}
          />
          Click to View PDF Document
        </div>
      );
    }

    // For non-PDF files (images)
    const fileResponse = document.file_response;
    const base64String = bufferToBase64(fileResponse);
    
    if (!base64String) {
      console.log("Failed to convert buffer to base64");
      return <p>Could not process file</p>;
    }

    try {
      const base64ToBinary = atob(base64String);
      const mimeType = 'image/png';
    
      return (
        <div>
          <img 
            src={`data:${mimeType};base64,${base64ToBinary}`}
            alt="Document Preview"
            className="document-thumbnail"
            onClick={() => openFullScreenImage(`data:${mimeType};base64,${base64ToBinary}`)} 
          />
        </div>
      );
    } catch (error) {
      console.error("Error processing file:", error);
      return <p>Error displaying file</p>;
    }
  };

  return (
    <>
      {/* Section B - Manual Uploads */}
      {manualTemplates.length > 0 && (
        <div className="manual-templates-section">
          {manualTemplates.map((document, templateIndex) => (
            <div key={templateIndex} className='complaince-tracker-employees-medical-upload-main-table-container'>
              <div className='complaince-tracker-employees-medical-upload-main-text-container'>
                <div className='complaince-tracker-employees-medical-upload-main-text-details-container'>
                  <div>
                    <h3>Section B - <span className="secondary-color-paragraph">Employee Medicals</span></h3>
                    <p>This section is the template that are all the employees that require medicals at {selectedCompanyName} and site {selectedDepartmentName} . </p>
                    <b>Template Details</b>
                    <p>Template Name : <span className="secondary-color-paragraph">{document.template_name}</span></p>
                    <p>Template Reference : <span className="secondary-color-paragraph">{document.template_reference}</span> </p>
                  </div>
                </div>

                <div className='complaince-tracker-employees-medical-upload-main-text-score-container'>
                  <div className='complaince-tracker-employees-medical-upload-main-text-score-container-container'>
                    <div>
                      <div>
                        <h3>Template Score</h3>
                      </div>
                      <div>
                        <p><span className="secondary-color-paragraph">{document.template_user_score || 0} / {document.template_score}</span></p>
                      </div>
                      <div>
                        <p><span className="secondary-color-paragraph">{document.user_template_percentage || 0} % </span></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Employee Table */}
              <div>
                <h3>Employee Submissions for <span className="secondary-color-paragraph">{document.template_name}</span></h3>
                
                <div className="manual-upload-employees-table-container">
                  {loadingEmployees ? (
                    <div className="loading-spinner-container">
                      <LiaSpinnerSolid className="lia-spin-solid-icon" />
                    </div>
                  ) : (
                    <table className="compliance-tracker-consultant-fill-in-table">
                      <colgroup>
                        <col style={{ width: "50px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "100px" }} />
                        <col style={{ width: "250px" }} />
                        <col style={{ width: "100px" }} />
                        <col style={{ width: "100px" }} />
                        <col style={{ width: "100px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                        <col style={{ width: "150px" }} />
                      </colgroup>

                      <thead>
                        <tr>
                          <th>View</th>
                          <th>Status</th>
                          <th>QA Actions</th>
                          <th>Employee ID</th>
                          <th>Employee Name</th>
                          <th>User Score</th>
                          <th>Template Score</th>
                          <th>Percentage</th>
                          <th>Upload Frequency</th>
                          <th>File Expiry Date</th>
                          <th>Validity</th>
                          <th>QA Status</th>
                          <th>QA Comment</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(!employees[document.template_id] || employees[document.template_id].length === 0) ? (
                          <tr>
                            <td colSpan="13" className="no-data-message">
                              <i>No employees found for company {selectedCompanyName} and site {selectedDepartmentName}.</i>
                            </td>
                          </tr>
                        ) : (
                          employees[document.template_id].map((employee, idx) => (
                            <tr key={idx}>
                              <td>
                                {employee.emp_submission_status === 'Saved' || employee.emp_submission_status === 'Not Completed' ? (
                                  <FaRegFolder 
                                    className='template-save-icon'
                                    onClick={() => handleViewEmployeeDocument(employee, document.template_id)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                ) : 
                                  <i>No File</i>
                                }
                              </td>

                              {/* Column - Employee Status*/}
                              <td>
                                {employee.emp_submission_status === 'Saved' ? (
                                  <div className="saved-status">
                                    <IoIosCloudDone /> {employee.emp_submission_status}
                                  </div>
                                ) : employee.emp_submission_status === 'Not Started' ? (
                                  <div className="not-saved-status">
                                    <MdCancel /> {employee.emp_submission_status}
                                  </div>
                                ) : employee.emp_response_status === 'Not Completed' ? (
                                  <div className="completed-status">
                                    <AiOutlineFileDone /> {employee.emp_response_status}
                                  </div>
                                ) : null}
                              </td>

                              {/* Column - Employee Accept or Reject  */}
                              <td>
                                <div className='complaince-tracker-employees-medical-upload-main-table-actions-main-container'>
                                  {/* Actions - Approve */}
                                  <div>
                                    <div>
                                      <TiTick 
                                        className='qa-approve-button'
                                        onClick={() => handleEmployeeApproveClick(employee, document.template_id)}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>

                                  {/* Actions - Reject*/}
                                  <div>
                                    <div>
                                      <RxCrossCircled 
                                        className='qa-reject-button'
                                        onClick={() => handleEmployeeRejectClick(employee, document.template_id)}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td>{employee.employee_id}</td>
                              <td>{`${employee.employee_name} ${employee.employee_surname}`}</td>
                              <td>{employee.emp_template_user_score}</td>
                              <td>{employee.emp_template_score}</td>
                              <td>{`${employee.emp_template_user_percentage || 0} %`}</td>
                              <td>{employee.file_period}</td>
                              <td>{employee.file_expiry_date}</td>
                              <td><p>Valid</p></td>
                              <td>{employee.qa_status || 'Not Started'}</td>
                              <td>{employee.qa_comment || 'Not Started'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Employee Document Viewer Modal */}
      {viewingEmployeeDocument && (
        <div 
          className="full-screen-image-overlay" 
          onClick={closeEmployeeDocumentViewer}
        >
          <div className="full-screen-document-container">
            <h3>Employee Document: {viewingEmployeeDocument.file_name || 'Document'}</h3>
            {loadingEmployeeDocument ? (
              <div className="loading-spinner-container">
                <LiaSpinnerSolid className="lia-spin-solid-icon" />
              </div>
            ) : (
              renderEmployeeDocument(viewingEmployeeDocument)
            )}

            <div className="document-details">
              <h3>Image Upload Details </h3>
              <p><strong>File Name:</strong> {viewingEmployeeDocument.file_name || 'N/A'}</p>
              <p><strong>Expiry Date:</strong> {viewingEmployeeDocument.file_expiry_date || 'N/A'}</p>
              <p><strong>QA Status:</strong> {viewingEmployeeDocument.qa_status || 'Not Started'}</p>
              <p><strong>QA Comment:</strong> {viewingEmployeeDocument.qa_comment || 'No comments'}</p>
              <p><strong>Submitted By:</strong> {`${viewingEmployeeDocument.filled_in_by_user_name || ''} ${viewingEmployeeDocument.filled_in_by_user_surname || ''}`}</p>
              <p><strong>Date Submitted:</strong> {new Date(viewingEmployeeDocument.date_submitted).toLocaleString()}</p>
            </div>
            
            <button 
              className="close-image-modal-btn" 
              onClick={closeEmployeeDocumentViewer}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Full Screen Image Modal */}
      {fullScreenImage && (
        <div 
          className="full-screen-image-overlay" 
          onClick={closeFullScreenImage}
        >
          <div className="full-screen-image-container">
            <img 
              src={fullScreenImage} 
              alt="Full Screen Preview" 
              className="full-screen-image"
              onClick={(e) => e.stopPropagation()} 
            />
            <button 
              className="close-image-modal-btn" 
              onClick={closeFullScreenImage}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Employee Toasters */}
      {showEmployeeApproveToaster && (
        <ApproveQAToasterComponent 
          onClose={handleEmployeeApproveClose}
          onSubmit={handleEmployeeApproveSubmit}
          documentId={selectedEmployeeForQA?.employee_id}
        />
      )}

      {showEmployeeRejectToaster && (
        <RejectQAToasterComponent
          onClose={handleEmployeeRejectClose}
          onSubmit={handleEmployeeRejectSubmit}
          documentId={selectedEmployeeForQA?.employee_id}
        />
      )}
    </>
  );
};

export default TemplateB;